import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { setToken } from '@/state/token/clientTokenSlice';
import { getTokenTimeRemaining } from '@/utils/tokenUtils';

// Minimum time between refresh attempts (30 seconds)
const MIN_REFRESH_INTERVAL_MS = 30000;
// How often to check if a refresh is needed (every 30 seconds)
const CHECK_INTERVAL_MS = 30000;
// Refresh when token has less than 2 minutes remaining
const REFRESH_THRESHOLD_SECONDS = 120;

/**
 * Hook to automatically refresh the authentication token before it expires
 *
 * Uses a polling approach instead of scheduling to avoid infinite loops
 * when the token dependency changes.
 */
export function useTokenRefresh() {
    const { getToken, isSignedIn } = useAuth();
    const dispatch = useAppDispatch();
    const token = useAppSelector(state => state.clientToken.token);

    const intervalRef = useRef<NodeJS.Timeout>();
    const lastRefreshTimeRef = useRef<number>(0);
    const isRefreshingRef = useRef<boolean>(false);
    // Store token in ref to avoid dependency issues
    const tokenRef = useRef<string | undefined>(token);

    // Keep tokenRef in sync
    useEffect(() => {
        tokenRef.current = token;
    }, [token]);

    const refreshToken = useCallback(async () => {
        // Prevent concurrent refreshes
        if (isRefreshingRef.current) {
            return;
        }

        // Throttle refreshes to prevent loops
        const now = Date.now();
        if (now - lastRefreshTimeRef.current < MIN_REFRESH_INTERVAL_MS) {
            return;
        }

        isRefreshingRef.current = true;
        lastRefreshTimeRef.current = now;

        try {
            console.log('Refreshing authentication token...');
            const newToken = await getToken({ template: 'Intelligenic' });

            if (newToken) {
                dispatch(setToken(newToken));
                console.log('Token refreshed successfully');
            }
        } catch (error) {
            console.error('Failed to refresh token:', error);
        } finally {
            isRefreshingRef.current = false;
        }
    }, [getToken, dispatch]);

    useEffect(() => {
        if (!isSignedIn) {
            return;
        }

        const checkAndRefresh = () => {
            const currentToken = tokenRef.current;
            if (!currentToken) {
                return;
            }

            const timeRemaining = getTokenTimeRemaining(currentToken);

            // Only refresh if token is close to expiring
            if (timeRemaining > 0 && timeRemaining < REFRESH_THRESHOLD_SECONDS) {
                refreshToken();
            }
        };

        // Initial check (with a small delay to avoid race conditions on mount)
        const initialTimeout = setTimeout(checkAndRefresh, 1000);

        // Set up periodic check
        intervalRef.current = setInterval(checkAndRefresh, CHECK_INTERVAL_MS);

        // Cleanup
        return () => {
            clearTimeout(initialTimeout);
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isSignedIn, refreshToken]);
}